<template>
    <div class="relative bg-white">
        <Hero
            :title="$t('our_services.flagship_service')"
            :lead="$t('filipino_workforce.lead')"
            :description="$t('filipino_workforce.description')"
            :illustration="require('~/assets/images/illustrations/filipino-workforce/page-header.png')"
            :decoration="require('~/assets/images/illustrations/decorator-dots-yellow.svg')"
            secondary-type
        />

        {{/* * */}}
        {{/* * */}}
        {{/* * */}}
        {{/* Filipino workforce in Hungary */}}
        {{/* * */}}
        <div class="container text-black pb-20 md:pt-8">
            <div class="grid grid-cols-12 gap-5">
                <div class="col-span-12 lg:col-span-10 lg:col-start-2 xl:col-span-8 xl:col-start-3">
                    <h2 class="title title-md text-center mb-10">{{ $t('generic.background') }}</h2>

                    <div
                        class="overflow-hidden relative transition-height"
                        :class="isDetailsSectionOpen ? 'max-h-full' : 'max-h-52'"
                    >
                        <div class="tinymce-content tinymce-content--indian-filipino-endpage" v-html="model.body"></div>

                        <div
                            v-if="!isDetailsSectionOpen"
                            class="
                                absolute
                                bottom-0
                                left-0
                                w-full
                                h-32
                                bg-gradient-to-b
                                from-transparent
                                via-white/90
                                to-white
                                z-10
                            "
                        ></div>
                        <Button
                            v-if="!isDetailsSectionOpen"
                            class="absolute bottom-0 left-1/2 -translate-x-1/2 z-20 p-button-text"
                            @click="$store.commit('indian/setIsDetailsSectionOpen', true)"
                        >
                            {{ $t('generic.read_more') }}
                        </Button>
                    </div>
                </div>
            </div>
        </div>

        {{/* * */}}
        {{/* * */}}
        {{/* * */}}
        {{/* Why filipino workforce */}}
        {{/* * */}}
        <div class="bg-gray-100 text-black py-16 md:py-20">
            <SectionWrapper :title="$t('filipino_workforce.pros.title')" centered-title class="container">
                <div class="grid grid-cols-12">
                    <div class="col-span-12 lg:col-span-8 lg:col-start-3">
                        <p class="sm:text-center mb-14">
                            {{ $t('filipino_workforce.pros.description') }}
                        </p>
                    </div>
                    <div class="col-span-12">
                        <div class="grid grid-cols-3 gap-x-5 gap-y-10 lg:max-w-[80%] mx-auto mb-20 lg:mb-20">
                            <div class="col-span-3 md:col-span-1">
                                <h3 class="sm:text-center title title-base text-purple-400 mb-5">
                                    {{ $t('filipino_workforce.pros.pro_1.title') }}
                                </h3>
                                <p class="sm:text-center">
                                    {{ $t('filipino_workforce.pros.pro_1.description') }}
                                </p>
                            </div>
                            <div class="col-span-3 md:col-span-1">
                                <h3 class="sm:text-center title title-base text-purple-400 mb-5">
                                    {{ $t('filipino_workforce.pros.pro_2.title') }}
                                </h3>
                                <p class="sm:text-center">
                                    {{ $t('filipino_workforce.pros.pro_2.description') }}
                                </p>
                            </div>
                            <div class="col-span-3 md:col-span-1">
                                <h3 class="sm:text-center title title-base text-purple-400 mb-5">
                                    {{ $t('filipino_workforce.pros.pro_3.title') }}
                                </h3>
                                <p class="sm:text-center">
                                    {{ $t('filipino_workforce.pros.pro_3.description') }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="text-center mt-8 lg:mt-12">
                    <NuxtLink :to="localePath('/contact')" class="p-component p-button p-button-primary">
                        <span class="p-button-label"> {{ $t('generic.contact_us') }} </span>
                    </NuxtLink>
                </div>
            </SectionWrapper>
        </div>

        {{/* * */}}
        {{/* * */}}
        {{/* * */}}
        {{/* Filipino workforce in Hungary */}}
        {{/* * */}}
        <div class="relative pt-16 lg:pt-20 pb-16 lg:pb-36 xl:pb-48 bg-gray-800 text-white">
            <div class="container">
                <SectionWrapper :title="$t('filipino_workforce.hiring.title')" centered-title>
                    <div class="grid grid-cols-1 gap-y-5 md:gap-y-16 lg:gap-y-20 pt-5 sm:pt-12">
                        <FeatureCard
                            :index="1"
                            :title="$t('filipino_workforce.hiring.import.title')"
                            :illustration="require('~/assets/images/illustrations/filipino-workforce/airplane.jpg')"
                            :decorator="require('~/assets/images/illustrations/decoration-07.svg')"
                            rounded-b-r
                            with-contact-button
                            :contact-btn-label="$t('generic.contact_cta_variants.variant_1')"
                            with-gap-between-text-and-image
                        >
                            <div v-html="$t('filipino_workforce.hiring.import.description')"></div>
                        </FeatureCard>

                        <FeatureCard
                            :index="2"
                            :title="$t('filipino_workforce.hiring.recruitment.title')"
                            :illustration="require('~/assets/images/illustrations/filipino-workforce/handshake.jpg')"
                            :decorator="require('~/assets/images/illustrations/decoration-08.svg')"
                            reversed
                            rounded-t-l
                            with-contact-button
                            :contact-btn-label="$t('generic.contact_cta_variants.variant_2')"
                            with-gap-between-text-and-image
                        >
                            <div
                                class="tinymce-content"
                                v-html="$t('filipino_workforce.hiring.recruitment.description')"
                            ></div>
                        </FeatureCard>

                        <FeatureCard
                            :index="3"
                            :title="$t('filipino_workforce.hiring.residence_permit.title')"
                            :illustration="require('~/assets/images/illustrations/filipino-workforce/paperwork.jpg')"
                            :decorator="require('~/assets/images/illustrations/decoration-07.svg')"
                            rounded-t-r
                            with-contact-button
                            :contact-btn-label="$t('generic.contact_cta_variants.variant_3')"
                            with-gap-between-text-and-image
                        >
                            <div
                                class="tinymce-content"
                                v-html="$t('filipino_workforce.hiring.residence_permit.description')"
                            ></div>
                        </FeatureCard>

                        <FeatureCard
                            :index="4"
                            :title="$t('filipino_workforce.hiring.starting_work.title')"
                            :illustration="
                                require('~/assets/images/illustrations/filipino-workforce/factory-worker.jpg')
                            "
                            :decorator="require('~/assets/images/illustrations/decoration-08.svg')"
                            rounded-t-l
                            reversed
                            with-contact-button
                            :contact-btn-label="$t('generic.contact_cta_variants.variant_4')"
                            with-gap-between-text-and-image
                        >
                            <div
                                class="tinymce-content"
                                v-html="$t('filipino_workforce.hiring.starting_work.description')"
                            ></div>
                        </FeatureCard>

                        <FeatureCard
                            :index="5"
                            :title="$t('filipino_workforce.hiring.aftercare.title')"
                            :illustration="require('~/assets/images/illustrations/filipino-workforce/helpdesk.jpg')"
                            :decorator="require('~/assets/images/illustrations/decoration-07.svg')"
                            rounded-t-r
                            with-contact-button
                            with-gap-between-text-and-image
                        >
                            <div
                                class="tinymce-content"
                                v-html="$t('filipino_workforce.hiring.aftercare.description')"
                            ></div>
                        </FeatureCard>
                    </div>
                </SectionWrapper>

                <div
                    class="sm:text-center sm:max-w-xl lg:max-w-2xl sm:mx-auto lg:pt-8 xl:pt-24"
                    v-html="$t('our_services.continuous_updates_text')"
                ></div>
            </div>
        </div>

        {{/* * */}}
        {{/* * */}}
        {{/* * */}}
        {{/* Partners */}}
        {{/* * */}}
        <div
            v-if="partners && partners.length"
            class="bg-white theme-light pt-16 md:pt-20 lg:pt-24 pb-16 md:pb-28 lg:pb-36"
        >
            <SectionPartners
                :partners="partners"
                :title="$t('case_studies.references_block_title')"
                theme-light
                class="container"
            />
            <div class="text-center">
                <NuxtLink :to="localePath('/contact')" class="p-component p-button p-button-primary">
                    <span class="p-button-label"> {{ $t('generic.contact_cta_variants.variant_4') }} </span>
                </NuxtLink>
            </div>
        </div>

        {{/* * */}}
        {{/* * */}}
        {{/* * */}}
        {{/* Testimonials */}}
        {{/* * */}}
        <div v-if="false" class="relative pb-12">
            <SectionTestimonials
                v-if="testimonials && testimonials.length"
                :testimonials="testimonials"
                class="relative z-10"
            />
            <div class="absolute bottom-0 left-0 w-full h-36 bg-gray-800 z-0"></div>
        </div>
    </div>
</template>

<script>
import Hero from '@/components/Heroes/Hero';
import FeatureCard from '@/components/Cards/FeatureCard/FeatureCard';
import SectionWrapper from '@/components/UI/SectionWrapper';
import SectionTestimonials from '@/components/Sections/SectionTestimonials/SectionTestimonials';
import SectionPartners from '@/components/Sections/SectionPartners/SectionPartners';
import { workforcePartners } from '~/data/partners';

export default {
    name: 'FilipinoWorkforce',
    components: {
        Hero,
        FeatureCard,
        SectionWrapper,
        SectionTestimonials,
        SectionPartners,
    },
    async asyncData({ $axios, error, i18n }) {
        let model = null;
        let testimonials = null;

        try {
            await Promise.all([$axios.$get('/content/by-slug/filipino-workforce'), $axios.$get('/testimonial?type=1')])
                .then(([contentRes, testimonialRes]) => {
                    if (contentRes.success === 1) {
                        model = contentRes.data;
                    }

                    if (testimonialRes.success === 1) {
                        testimonials = testimonialRes.data;
                    }
                })
                .catch((err) => {
                    console.log(err);
                });
        } catch (err) {
            console.log(err);
        }

        if (!model) {
            return error({ statusCode: 404, message: i18n.t('error.page_not_found.title') });
        }

        return { model, testimonials };
    },
    data() {
        return {
            partners: workforcePartners,
        };
    },
    computed: {
        isDetailsSectionOpen() {
            return this.$store.state.indian.isDetailsSectionOpen;
        },
    },
};
</script>

<style scoped></style>
